import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import LogoDark from "../../assets/img/logo-dark.png";
import LogoIconDark from "../../assets/img/logo-icon-dark.png";
import LogoIconWhite from "../../assets/img/logo-icon-white.png";
import { FaUser, FaSignOutAlt, FaCalendarAlt, FaGift } from "react-icons/fa";
import AuthModal from "../AuthModal";

const Header = () => {
  const [header, setHeader] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [prefillEmail, setPrefillEmail] = useState("");
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Abrir modal de login si viene ?login=email
  useEffect(() => {
    const loginEmail = searchParams.get("login");
    if (loginEmail) {
      setPrefillEmail(loginEmail);
      setShowAuthModal(true);
      searchParams.delete("login");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const handleScroll = () => {
      window.scrollY > 50 ? setHeader(true) : setHeader(false);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const logoWidth = 160;
  const logoHeight = 40;

  return (
    <header
      id="header"
      className={`${
        header ? "bg-white py-3 shadow-md" : "bg-transparent py-6"
      } fixed z-50 w-full transition-all duration-500`}
    >
      <div className="lg:container lg:mx-auto flex flex-col items-center justify-between lg:flex-row">
        <div className="flex justify-between w-full px-4">
          <a href="/" className="flex items-center">
            <img
              className={`w-auto transition-all duration-500 ${header ? "h-[52px]" : "h-[64px]"}`}
              src={header ? LogoIconDark : LogoIconWhite}
              alt="Estancia La Alameda - Hotel Boutique en Chascomús"
              width={header ? 52 : 64}
              height={header ? 52 : 64}
            />
          </a>
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`focus:outline-none ml-4 cursor-pointer ${
                header ? "text-primary" : "text-white"
              }`}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d={isOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16m-7 6h7"}
                />
              </svg>
            </button>
          </div>
        </div>

        <nav
          className={`${
            isOpen
              ? "fixed inset-0 top-0 left-0 w-screen h-screen flex flex-col items-center justify-center gap-8 bg-white z-50 animate-slideDown"
              : "hidden"
          } lg:static lg:w-auto lg:h-auto lg:flex lg:flex-row lg:items-center lg:gap-x-8 lg:py-0 lg:bg-transparent font-tertiary tracking-[3px] text-[15px] uppercase ${
            header
              ? "text-primary"
              : "lg:text-white text-black"
          }`}
        >
          {/* Logo and close button for fullscreen mobile menu */}
          {isOpen && (
            <>
              <a href="/" className="absolute top-8 left-1/2 -translate-x-1/2 lg:hidden">
                <img
                  className="w-[160px]"
                  src={LogoDark}
                  alt="Estancia La Alameda"
                  width={logoWidth}
                  height={logoHeight}
                />
              </a>
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-8 right-6 text-primary lg:hidden cursor-pointer"
              >
                <svg
                  className="w-7 h-7"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </>
          )}
          <a
            href="/"
            className="hover:text-accent transition duration-300 ease-in-out"
          >
            Home
          </a>
          <a
            href="/hotel"
            className="hover:text-accent transition duration-300 ease-in-out"
          >
            Hotel
          </a>
          <a
            href="/glamping"
            className="hover:text-accent transition duration-300 ease-in-out"
          >
            Glamping
          </a>
          <a
            href="/paquetes"
            className="hover:text-accent transition duration-300 ease-in-out"
          >
            Paquetes
          </a>
          <a
            href="/servicios/restaurante"
            className="hover:text-accent transition duration-300 ease-in-out"
          >
            Restaurante
          </a>
          <a
            href="/servicios/diaDeCampo"
            className="hover:text-accent transition duration-300 ease-in-out whitespace-nowrap"
          >
            Día de campo
          </a>
          <a
            href="/servicios/eventos"
            className="hover:text-accent transition duration-300 ease-in-out"
          >
            Eventos
          </a>
          <a
            href="/gift-card"
            className="hover:text-accent transition duration-300 ease-in-out whitespace-nowrap"
          >
            Gift Card
          </a>

          {/* Menú de usuario */}
          {isAuthenticated ? (
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 hover:text-accent transition duration-300 ease-in-out"
              >
                <FaUser />
                <span className="hidden lg:inline">{user?.name}</span>
              </button>

              {/* Dropdown del menú */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                  <button
                    onClick={() => {
                      navigate('/mi-perfil');
                      setShowUserMenu(false);
                      setIsOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-primary hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FaUser />
                    Mi Perfil
                  </button>
                  <button
                    onClick={() => {
                      navigate('/mis-reservas');
                      setShowUserMenu(false);
                      setIsOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-primary hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FaCalendarAlt />
                    Mis Reservas
                  </button>
                  <button
                    onClick={() => {
                      navigate('/mis-gift-cards');
                      setShowUserMenu(false);
                      setIsOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-primary hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FaGift />
                    Mis Gift Cards
                  </button>
                  <hr className="my-2 border-gray-200" />
                  <button
                    onClick={() => {
                      logout();
                      setShowUserMenu(false);
                      setIsOpen(false);
                      navigate('/');
                    }}
                    className="w-full text-left px-4 py-2 text-primary hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FaSignOutAlt />
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => setShowAuthModal(true)}
              className="hover:text-accent transition duration-300 ease-in-out flex items-center gap-2 font-tertiary tracking-[3px] text-[15px] uppercase"
            >
              <FaUser />
              <span>Ingresar</span>
            </button>
          )}
        </nav>
      </div>

      {/* Modal de autenticación */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          setPrefillEmail("");
        }}
        onSuccess={() => {
          setShowAuthModal(false);
          setPrefillEmail("");
          setIsOpen(false);
        }}
        prefillEmail={prefillEmail}
      />
    </header>
  );
};

export default Header;
